export default function getTestName(test) {
  return (typeof test === "string" ? test : test?.name || test?.test_name || "").trim();
}

export function parseNextPage(nextPageLink, currentPage) {
  if (!nextPageLink) return null;
  try {
    const pageParam = String(nextPageLink).split("page=")[1];
    if (!pageParam) return Number(currentPage) + 1;
    return pageParam.split("&")[0];
  } catch (e) {
    return Number(currentPage) + 1;
  }
}

/** Tooltip from test.display / category / TAT — same fields as AnalysisRequest suggestions. */
export function buildTestTooltip(test) {
  if (!test || typeof test === "string") return "";
  const lines = [];
  if (Array.isArray(test.display)) {
    test.display.forEach(({ title, value }) => {
      if (title && value != null && value !== "") {
        lines.push(`${title}: ${value}`);
      }
    });
  }
  if (test.test_category) lines.push(`Category: ${test.test_category}`);
  if (test.TAT || test.tat) lines.push(`TAT: ${test.TAT || test.tat}`);
  return lines.join("\n");
}

export function getGroupName(group, index = 0) {
  return group?.group_name || group?.name || group?.title || `Group ${index + 1}`;
}

export function getGroupId(group) {
  if (!group || typeof group !== "object") return "";
  return String(group.group_id ?? group.groupId ?? "").trim();
}

export function getGroupTests(group) {
  if (Array.isArray(group?.test_list)) return group.test_list;
  if (Array.isArray(group?.tests)) return group.tests;
  return [];
}

/** Parse added_on (YYYY-MM-DD); empty/missing sorts last for "recent". */
function addedOnValue(test) {
  if (!test || typeof test !== "object") return "";
  return String(test.added_on || "").trim();
}

/** Client sort: added_on (recent) or name (az/za). Empty added_on sorts last. */
export function sortTestsByOption(tests, sortBy = "recent") {
  const list = Array.isArray(tests) ? [...tests] : [];
  const byName = (a, b) => getTestName(a).localeCompare(getTestName(b), undefined, { sensitivity: "base" });

  if (sortBy === "az") return list.sort(byName);
  if (sortBy === "za") return list.sort((a, b) => byName(b, a));

  return list.sort((a, b) => {
    const dateA = addedOnValue(a);
    const dateB = addedOnValue(b);
    if (!dateA && !dateB) return byName(a, b);
    if (!dateA) return 1;
    if (!dateB) return -1;
    const byDate = dateB.localeCompare(dateA);
    if (byDate !== 0) return byDate;
    return byName(a, b);
  });
}

const REQUEST_STATUS_DISPLAY = {
  pending: "CSM Review",
  approved: "Approved",
  accepted: "Approved",
  rejected: "Rejected",
};

const REQUEST_TYPE_DISPLAY = {
  unlock: "Unlock tests",
  "unlock tests": "Unlock tests",
  new: "New Test",
  "new test": "New Test",
};

/** Map API status to UI labels. Prefer `status` over next_step. */
export function mapRequestStatus(status) {
  const key = String(status || "").trim().toLowerCase();
  if (!key) return "—";
  if (REQUEST_STATUS_DISPLAY[key]) return REQUEST_STATUS_DISPLAY[key];
  if (key.includes("pending") || key.includes("csm")) return REQUEST_STATUS_DISPLAY.pending;
  if (key.includes("approv") || key.includes("accept")) return REQUEST_STATUS_DISPLAY.approved;
  if (key.includes("reject")) return REQUEST_STATUS_DISPLAY.rejected;
  return String(status);
}

export function mapRequestType(requestType) {
  const key = String(requestType || "").trim().toLowerCase();
  if (!key) return "—";
  if (REQUEST_TYPE_DISPLAY[key]) return REQUEST_TYPE_DISPLAY[key];
  return String(requestType);
}

export function isApprovedStatus(status = "") {
  const key = String(status).toLowerCase();
  return key.includes("approved") || key.includes("accept");
}

export function isRejectedStatus(status = "") {
  return String(status).toLowerCase().includes("reject");
}

/** Pending unlock panel — exclude explicit "new test" requests. */
export function isUnlockRequest(request = {}) {
  const rawType = request.type ?? request.request_type ?? "";
  const key = String(rawType).trim().toLowerCase();
  if (key === "new" || key === "new test") return false;
  if (key === "unlock" || key === "unlock tests") return true;
  return !key;
}

export function getUnlockRequestTestName(request = {}) {
  return (request.test_name || request.requested_name || "").trim();
}

export const TEST_LEVEL_SECTIONS = [
  { key: "", label: "My Tests" },
  { key: "basic", label: "Basic" },
  { key: "advanced", label: "Advanced" },
];

export function normalizeTestLevel(test) {
  if (!test || typeof test !== "object") return "";
  const level = String(test.test_level ?? "").trim().toLowerCase();
  if (level === "basic" || level === "advanced") return level;
  return "";
}

export function groupTestsByLevel(tests) {
  const buckets = {
    "": [],
    basic: [],
    advanced: [],
  };
  (Array.isArray(tests) ? tests : []).forEach((test) => {
    buckets[normalizeTestLevel(test)].push(test);
  });
  return TEST_LEVEL_SECTIONS
    .map(({ key, label }) => ({ key, label, tests: buckets[key] }))
    .filter((section) => section.tests.length > 0);
}

/** Map API pagination fields to Test Directory list state. */
export function buildPaginationState(result = {}, fallbackPage = 1) {
  const currentPage = Number(result.currentPage) || fallbackPage;
  const totalPages = Number(result.totalPages) || 1;
  const pageSize = Number(result.pageSize) || 10;
  const totalCount = result.totalCount != null
    ? Number(result.totalCount) || 0
    : totalPages * pageSize;

  return {
    currentPage,
    totalPages,
    pageSize,
    totalCount,
    nextPageLink: result.nextPageLink || "",
    prevPageLink: result.prevPageLink || "",
  };
}
